import { useState, useEffect } from "react";
import { FileText, Plus, Search, Eye, Edit2, Download, Printer, X, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  unpaid: "bg-red-100 text-red-700",
  partial: "bg-amber-100 text-amber-700",
  paid: "bg-green-100 text-green-700"
};

export default function InvoiceManagement() {
  const { token, user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);

  const [form, setForm] = useState({
    customer_id: "", due_date: "", notes: "", gst_type: "cgst_sgst",
    items: [{ product_id: "", product_name: "", quantity: 1, unit: "kg", unit_price: 0, gst_percent: 18 }]
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: 0, payment_date: new Date().toISOString().split("T")[0], payment_method: "bank_transfer", reference_number: "", notes: ""
  });

  const canEdit = ["owner", "manager"].includes(user?.role);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, customersRes, productsRes] = await Promise.all([
        fetch(`${API}/api/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/products`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (invoicesRes.ok) setInvoices(await invoicesRes.json());
      if (customersRes.ok) setCustomers(await customersRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
    } catch (e) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const fetchInvoiceDetail = async (invoiceId) => {
    try {
      const res = await fetch(`${API}/api/invoices/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { setInvoiceDetail(await res.json()); setShowDetailModal(true); }
    } catch (e) { toast.error("Failed to load invoice details"); }
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { product_id: "", product_name: "", quantity: 1, unit: "kg", unit_price: 0, gst_percent: 18 }] });
  };

  const removeItem = (index) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    if (field === "product_id") {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].product_name = product.name;
        newItems[index].unit_price = product.price?.domestic || 0;
      }
    }
    setForm({ ...form, items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = form.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const totalGst = form.items.reduce((sum, item) => sum + (item.quantity * item.unit_price * item.gst_percent / 100), 0);
    return { subtotal, totalGst, grandTotal: subtotal + totalGst };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Please select a customer"); return; }
    if (form.items.some(item => !item.product_name || item.quantity <= 0)) { toast.error("Please fill all item details"); return; }

    const method = selectedInvoice ? "PUT" : "POST";
    const url = selectedInvoice ? `${API}/api/invoices/${selectedInvoice.id}` : `${API}/api/invoices`;
    try {
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(selectedInvoice ? "Invoice updated" : "Invoice created");
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save invoice");
      }
    } catch (e) { toast.error("Failed to save invoice"); }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!invoiceDetail) return;
    try {
      const res = await fetch(`${API}/api/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          customer_id: invoiceDetail.customer_id,
          invoice_id: invoiceDetail.id,
          ...paymentForm
        })
      });
      if (res.ok) {
        toast.success("Payment recorded");
        setShowPaymentModal(false);
        setShowDetailModal(false);
        fetchData();
        setPaymentForm({ amount: 0, payment_date: new Date().toISOString().split("T")[0], payment_method: "bank_transfer", reference_number: "", notes: "" });
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to record payment");
      }
    } catch (e) { toast.error("Failed to record payment"); }
  };

  const openPaymentModal = () => {
    if (invoiceDetail) {
      const balance = invoiceDetail.grand_total - (invoiceDetail.amount_paid || 0);
      setPaymentForm({ ...paymentForm, amount: balance });
      setShowPaymentModal(true);
    }
  };

  const resetForm = () => {
    setSelectedInvoice(null);
    setForm({
      customer_id: "", due_date: "", notes: "", gst_type: "cgst_sgst",
      items: [{ product_id: "", product_name: "", quantity: 1, unit: "kg", unit_price: 0, gst_percent: 18 }]
    });
  };

  const printInvoice = () => {
    if (!invoiceDetail) return;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoiceDetail.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
          .header { display: flex; justify-content: space-between; border-bottom: 2px solid #1a472a; padding-bottom: 20px; margin-bottom: 30px; }
          .company { font-size: 24px; font-weight: bold; color: #1a472a; }
          .company-info { font-size: 12px; color: #666; margin-top: 5px; }
          .invoice-title { font-size: 28px; color: #1a472a; text-align: right; }
          .invoice-number { font-size: 14px; color: #666; text-align: right; }
          .details { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .customer, .invoice-info { width: 45%; }
          .section-title { font-weight: bold; color: #1a472a; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th { background: #1a472a; color: white; padding: 12px; text-align: left; }
          td { padding: 10px; border-bottom: 1px solid #ddd; }
          .text-right { text-align: right; }
          .totals { width: 300px; margin-left: auto; }
          .totals td { padding: 8px; }
          .grand-total { font-size: 18px; font-weight: bold; background: #f5f5f5; }
          .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; text-align: center; }
          .status { display: inline-block; padding: 5px 15px; border-radius: 20px; font-weight: bold; text-transform: uppercase; }
          .status-unpaid { background: #fee2e2; color: #dc2626; }
          .status-partial { background: #fef3c7; color: #d97706; }
          .status-paid { background: #dcfce7; color: #16a34a; }
          @media print { body { margin: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="company">Sri Srinivasa Coir Industry</div>
            <div class="company-info">
              S Rayavaram Mandal, 17/1, Koruprolu<br/>
              Andhra Pradesh 531083, India<br/>
              Phone: +91 9666195695, +91 7013696728<br/>
              Email: siddardh3.v@gmail.com
            </div>
          </div>
          <div>
            <div class="invoice-title">INVOICE</div>
            <div class="invoice-number">${invoiceDetail.invoice_number}</div>
            <div style="margin-top: 10px;">
              <span class="status status-${invoiceDetail.status}">${invoiceDetail.status}</span>
            </div>
          </div>
        </div>
        
        <div class="details">
          <div class="customer">
            <div class="section-title">Bill To</div>
            <strong>${invoiceDetail.customer_name}</strong><br/>
            ${invoiceDetail.customer?.address || ""}<br/>
            ${invoiceDetail.customer?.phone || ""}<br/>
            ${invoiceDetail.customer?.gst_number ? `GST: ${invoiceDetail.customer.gst_number}` : ""}
          </div>
          <div class="invoice-info">
            <div class="section-title">Invoice Details</div>
            <strong>Date:</strong> ${new Date(invoiceDetail.created_at).toLocaleDateString()}<br/>
            ${invoiceDetail.due_date ? `<strong>Due Date:</strong> ${invoiceDetail.due_date}<br/>` : ""}
            ${invoiceDetail.sales_order_number ? `<strong>SO Ref:</strong> ${invoiceDetail.sales_order_number}` : ""}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th class="text-right">Quantity</th>
              <th class="text-right">Unit Price</th>
              <th class="text-right">GST</th>
              <th class="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${invoiceDetail.items?.map(item => `
              <tr>
                <td>${item.product_name}</td>
                <td class="text-right">${item.quantity} ${item.unit}</td>
                <td class="text-right">₹${item.unit_price.toLocaleString()}</td>
                <td class="text-right">${item.gst_percent}%</td>
                <td class="text-right">₹${(item.quantity * item.unit_price).toLocaleString()}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <table class="totals">
          <tr><td>Subtotal</td><td class="text-right">₹${invoiceDetail.subtotal?.toLocaleString()}</td></tr>
          ${invoiceDetail.gst_type === "cgst_sgst" ? `
            <tr><td>CGST</td><td class="text-right">₹${invoiceDetail.cgst?.toLocaleString()}</td></tr>
            <tr><td>SGST</td><td class="text-right">₹${invoiceDetail.sgst?.toLocaleString()}</td></tr>
          ` : `
            <tr><td>IGST</td><td class="text-right">₹${invoiceDetail.igst?.toLocaleString()}</td></tr>
          `}
          <tr class="grand-total"><td>Grand Total</td><td class="text-right">₹${invoiceDetail.grand_total?.toLocaleString()}</td></tr>
          ${invoiceDetail.amount_paid > 0 ? `
            <tr><td>Amount Paid</td><td class="text-right">₹${invoiceDetail.amount_paid?.toLocaleString()}</td></tr>
            <tr><td><strong>Balance Due</strong></td><td class="text-right"><strong>₹${(invoiceDetail.grand_total - invoiceDetail.amount_paid).toLocaleString()}</strong></td></tr>
          ` : ""}
        </table>

        ${invoiceDetail.notes ? `<div style="margin-top: 20px; padding: 15px; background: #f9f9f9; border-radius: 5px;"><strong>Notes:</strong> ${invoiceDetail.notes}</div>` : ""}

        <div class="footer">
          Thank you for your business!<br/>
          Sri Srinivasa Coir Industry - Quality Coir Products Since 2006
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  const filtered = invoices.filter(inv => {
    const matchesSearch = inv.invoice_number.toLowerCase().includes(search.toLowerCase()) ||
      inv.customer_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const { subtotal, totalGst, grandTotal } = calculateTotals();

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-forest/20 border-t-forest rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="invoice-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-serif font-bold text-navy">Invoices</h1>
          <p className="text-slate-500 text-sm font-sans">Create and manage invoices</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-forest hover:bg-forest/90" data-testid="add-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> New Invoice
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by invoice # or customer..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-invoices" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(invoice => (
              <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                <TableCell>{invoice.customer_name}</TableCell>
                <TableCell>{new Date(invoice.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right font-medium">₹{invoice.grand_total?.toLocaleString()}</TableCell>
                <TableCell className="text-right">₹{(invoice.amount_paid || 0).toLocaleString()}</TableCell>
                <TableCell><Badge className={statusColors[invoice.status]}>{invoice.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => fetchInvoiceDetail(invoice.id)} data-testid={`view-invoice-${invoice.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && (
          <div className="py-12 text-center text-slate-500">
            {search || statusFilter !== "all" ? "No invoices match your filters" : "No invoices yet"}
          </div>
        )}
      </Card>

      {/* Create Invoice Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedInvoice ? "Edit Invoice" : "New Invoice"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={v => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="select-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="input-due-date" />
              </div>
            </div>

            <div>
              <Label>GST Type</Label>
              <Select value={form.gst_type} onValueChange={v => setForm({ ...form, gst_type: v })}>
                <SelectTrigger data-testid="select-gst-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cgst_sgst">CGST + SGST (Intra-state)</SelectItem>
                  <SelectItem value="igst">IGST (Inter-state)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {form.items.map((item, index) => (
                  <div key={index} className="grid gap-2 sm:grid-cols-6 p-3 bg-slate-50 rounded-lg items-end">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Product</Label>
                      <Select value={item.product_id} onValueChange={v => updateItem(index, "product_id", v)}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>
                          {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" min="0.1" step="0.1" className="h-9" value={item.quantity} onChange={e => updateItem(index, "quantity", parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">Unit</Label>
                      <Select value={item.unit} onValueChange={v => updateItem(index, "unit", v)}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="tons">tons</SelectItem>
                          <SelectItem value="blocks">blocks</SelectItem>
                          <SelectItem value="rolls">rolls</SelectItem>
                          <SelectItem value="pieces">pieces</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Price (₹)</Label>
                      <Input type="number" min="0" className="h-9" value={item.unit_price} onChange={e => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">GST %</Label>
                        <Input type="number" min="0" max="28" className="h-9" value={item.gst_percent} onChange={e => updateItem(index, "gst_percent", parseFloat(e.target.value) || 0)} />
                      </div>
                      {form.items.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" className="text-red-500 h-9" onClick={() => removeItem(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between"><span>Subtotal:</span><span className="font-medium">₹{subtotal.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>GST:</span><span className="font-medium">₹{totalGst.toLocaleString()}</span></div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total:</span><span>₹{grandTotal.toLocaleString()}</span></div>
                </div>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="input-notes" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-forest hover:bg-forest/90" data-testid="submit-invoice">{selectedInvoice ? "Update" : "Create"} Invoice</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {invoiceDetail && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-navy">{invoiceDetail.invoice_number}</h3>
                  <p className="text-slate-500">{invoiceDetail.customer_name}</p>
                  {invoiceDetail.sales_order_number && <p className="text-sm text-slate-400">SO: {invoiceDetail.sales_order_number}</p>}
                </div>
                <Badge className={statusColors[invoiceDetail.status]}>{invoiceDetail.status}</Badge>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 text-sm">
                <div><span className="text-slate-500">Created:</span> {new Date(invoiceDetail.created_at).toLocaleDateString()}</div>
                {invoiceDetail.due_date && <div><span className="text-slate-500">Due:</span> {invoiceDetail.due_date}</div>}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoiceDetail.items?.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                      <TableCell className="text-right">₹{item.unit_price}</TableCell>
                      <TableCell className="text-right">{item.gst_percent}%</TableCell>
                      <TableCell className="text-right font-medium">₹{(item.quantity * item.unit_price).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between"><span>Subtotal:</span><span>₹{invoiceDetail.subtotal?.toLocaleString()}</span></div>
                  {invoiceDetail.gst_type === "cgst_sgst" ? (
                    <>
                      <div className="flex justify-between"><span>CGST:</span><span>₹{invoiceDetail.cgst?.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span>SGST:</span><span>₹{invoiceDetail.sgst?.toLocaleString()}</span></div>
                    </>
                  ) : (
                    <div className="flex justify-between"><span>IGST:</span><span>₹{invoiceDetail.igst?.toLocaleString()}</span></div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total:</span><span>₹{invoiceDetail.grand_total?.toLocaleString()}</span></div>
                  {invoiceDetail.amount_paid > 0 && (
                    <>
                      <div className="flex justify-between text-green-600"><span>Paid:</span><span>₹{invoiceDetail.amount_paid?.toLocaleString()}</span></div>
                      <div className="flex justify-between font-bold"><span>Balance:</span><span>₹{(invoiceDetail.grand_total - invoiceDetail.amount_paid).toLocaleString()}</span></div>
                    </>
                  )}
                </div>
              </div>

              {invoiceDetail.notes && <div className="text-sm p-3 bg-slate-50 rounded"><span className="text-slate-500">Notes:</span> {invoiceDetail.notes}</div>}

              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button size="sm" variant="outline" onClick={printInvoice} data-testid="print-invoice-btn">
                  <Printer className="w-4 h-4 mr-1" /> Print / PDF
                </Button>
                {invoiceDetail.status !== "paid" && (
                  <Button size="sm" onClick={openPaymentModal} data-testid="record-payment-btn">
                    <CreditCard className="w-4 h-4 mr-1" /> Record Payment
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div>
              <Label>Amount (₹) *</Label>
              <Input type="number" min="0.01" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })} required data-testid="input-payment-amount" />
            </div>
            <div>
              <Label>Payment Date *</Label>
              <Input type="date" value={paymentForm.payment_date} onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} required data-testid="input-payment-date" />
            </div>
            <div>
              <Label>Payment Method *</Label>
              <Select value={paymentForm.payment_method} onValueChange={v => setPaymentForm({ ...paymentForm, payment_method: v })}>
                <SelectTrigger data-testid="select-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference Number</Label>
              <Input value={paymentForm.reference_number} onChange={e => setPaymentForm({ ...paymentForm, reference_number: e.target.value })} placeholder="Transaction ID / Cheque No." data-testid="input-reference" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={paymentForm.notes} onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })} rows={2} data-testid="input-payment-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-forest hover:bg-forest/90" data-testid="submit-payment">Record Payment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
